import { Component, effect, EventEmitter, Output } from '@angular/core';
import { BaseInputComponent } from 'app/components/ui-kit/inputs/base-input/base-input.component';
import { SvgLoaderComponent } from 'app/components/ui-kit/images/svg-loader/svg-loader.component';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { environment } from 'env/environment';

import { ConversationService } from 'app/services/conversation.service';
import { TimeAndDateService } from 'app/services/time-and-date-service';
import { DeviceService } from 'app/services/device.service';
import { AppStateService } from 'app/services/app-state.service';
import { FlightOptionService } from 'app/services/flight-option-service';
import { buildQueryPayload, parseAiResponse } from './utils/query-utils';
import { shouldResetFlightOptionId } from '../../utils/data-model-utils';
import { AlertMessageComponent } from "app/components/ui-kit/alerts/alert-message/alert-message.component";

@Component({
  selector: 'app-agent-keyboard-io',
  standalone: true,
  imports: [BaseInputComponent, SvgLoaderComponent, CommonModule, FormsModule, AlertMessageComponent],
  templateUrl: './agent-keyboard.component.html',
  styleUrl: './agent-keyboard.component.scss'
})
export class AgentKeyboardComponent {
  @Output() onKeyboardFocusChange = new EventEmitter<boolean>();
  currentConversation: any[] = [];
  currentDataModel: any;
  user: any;

  query = '';
  queryError: string | null = null;
  isProcessingQuery = false;
  dynamicStatusText = 'Processing...';
  dynamicPlaceholderText = 'Start typing with AI Agent...';
  isKeyboardFocused = false;
  deviceType: string;

  constructor(
    private conversationService: ConversationService,
    private timeService: TimeAndDateService,
    private deviceService: DeviceService,
    public appState: AppStateService,
    private flightOptions: FlightOptionService
  ) {
    this.deviceType = this.deviceService.getDeviceType();
    effect(() => {
      this.currentConversation = this.appState.agent.currentConversation();
    });
    effect(() => {
      this.currentDataModel = this.appState.agent.currentDataModel();
    });
    effect(() => {
      this.user = this.appState.user.profile();
    });
  }

  onFocus() {
    if (this.isKeyboardFocused === false && this.deviceType === 'mobile') {
      this.isKeyboardFocused = true;
      this.onKeyboardFocusChange.emit(true);
    }
  }

  onBackdropClick() {
    this.onBlur();
  }

  onBlur() {
    if (this.deviceType === 'mobile') {
      this.onKeyboardFocusChange.emit(false);
      this.isKeyboardFocused = false;
    }
  }

  getInputPlaceholder() {
    if (this.currentConversation && this.currentConversation.length > 0) {
      this.dynamicPlaceholderText = 'Describe your flight...';
    } else {
      this.dynamicPlaceholderText = 'Start typing with AI Agent...';
    }
    return this.dynamicPlaceholderText;
  }

  onSendQuery(): void {
    if (this.query.trim().length > 0) {
      this.appState.agent.isHandlingQuery.set(true);
      this.onBlur();
      void this.handleUserQuery();
      const isoTimestamp = this.timeService.getIsoTimestamp();
      this.appState.agent.currentConversation.update(convo => [
        ...convo,
        { timestamp: isoTimestamp, user: this.query, agent: '' }
      ]);
      this.isProcessingQuery = true;
      this.query = '';
    } else {
      this.queryError = 'Your message is too short';
    }
  }

  async handleUserQuery() {
    const payload = buildQueryPayload(
      this.query,
      this.currentConversation,
      this.currentDataModel,
      this.user
    );
    this.dynamicStatusText = 'Reasoning...';

    try {
      const response = await fetch(
        `https://${environment.apiBaseURL}.cloudfunctions.net/getAiResponseText`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        this.queryError = errorText;
        console.error('Query fetch failed:', response.status, errorText);
        return;
      }

      const responseString = await response.text();
      const { dataModel, responseText, intent } = parseAiResponse(responseString);

      const oldModel = this.currentDataModel;
      if (shouldResetFlightOptionId(oldModel, dataModel)) {
        dataModel.userFlightOptionId = null;
      } else {
        dataModel.userFlightOptionId = oldModel?.userFlightOptionId ?? null;
      }

      this.appState.agent.currentDataModel.set(dataModel);
      this.appState.agent.currentIntent.set(intent);

      const isValid = await this.flightOptions.isFlightDataModelValid(dataModel);
      if (isValid) {
        this.dynamicStatusText = 'Agent Searching...';
        await this.conversationService.updateCurrentConversationData(
          this.currentConversation,
          dataModel,
          intent
        );
      } else {
        this.dynamicStatusText = 'Agent Responding...';
        const updatedConvo = this.currentConversation.map((msg, i, arr) =>
          i !== arr.length - 1 ? msg : { ...msg, agent: responseText }
        );
        await this.conversationService.updateCurrentConversationData(
          updatedConvo,
          dataModel,
          intent
        );
        this.appState.agent.currentConversation.set(updatedConvo);
      }
    } catch (error) {
      console.error('Error handling query:', error);
      this.queryError = (error as Error).message;
    } finally {
      this.isProcessingQuery = false;
      this.dynamicStatusText = 'Processing...';
      this.appState.agent.isHandlingQuery.set(false);
    }
  }
}
